import type { DatabaseLike, DbState } from '../db/types';
import { CATALOG_QUERY, CHAPTER_VERSES_QUERY } from '../reader/queries';

type Testament = 'OT' | 'NT';

export interface MockBook {
  id: number;
  name: string;
  testament: Testament;
  chapterCount: number;
}

export interface MockDatabaseOptions {
  status?: DbState;
  books?: MockBook[];
  deferInit?: boolean;
  initGate?: DeferredGate;
}

type MockQueryRow = Record<string, unknown>;
type DeferredGate = { promise: Promise<void>; resolve: () => void };

export const NAVIGATION_FIXTURE = 'navigation';

export const DEFAULT_MOCK_BOOKS: MockBook[] = [
  { id: 1, name: 'Genesis', testament: 'OT', chapterCount: 3 },
  { id: 19, name: 'Psalms', testament: 'OT', chapterCount: 3 },
  { id: 40, name: 'Matthew', testament: 'NT', chapterCount: 28 },
  { id: 41, name: 'Mark', testament: 'NT', chapterCount: 16 },
  { id: 42, name: 'Luke', testament: 'NT', chapterCount: 24 },
  { id: 43, name: 'John', testament: 'NT', chapterCount: 21 },
  { id: 44, name: 'Acts', testament: 'NT', chapterCount: 28 },
  { id: 45, name: 'Romans', testament: 'NT', chapterCount: 16 },
  { id: 52, name: '1 Thessalonians', testament: 'NT', chapterCount: 5 },
  { id: 18, name: 'Job', testament: 'OT', chapterCount: 42 },
  { id: 20, name: 'Proverbs', testament: 'OT', chapterCount: 31 },
  { id: 6, name: 'Joshua', testament: 'OT', chapterCount: 24 },
  { id: 23, name: 'Isaiah', testament: 'OT', chapterCount: 66 },
];

export function usesMockDatabaseFixture(search: string): boolean {
  return new URLSearchParams(search).get('fixture') === NAVIGATION_FIXTURE;
}

export function createMockDatabaseClient(
  options: MockDatabaseOptions = {},
): DatabaseLike {
  const books = options.books ?? DEFAULT_MOCK_BOOKS;
  const initGate = options.initGate ?? createDeferredGate();
  const initialStatus = options.status ?? { state: 'idle' };

  if (!options.deferInit && !options.initGate) {
    initGate.resolve();
  }

  return new MockDatabaseClient(books, initGate, initialStatus, Boolean(options.status));
}

export function createControlledMockDatabaseClient(
  options: Omit<MockDatabaseOptions, 'deferInit'> = {},
): { client: DatabaseLike; resolveInit: () => void } {
  const gate = createDeferredGate();
  const client = createMockDatabaseClient({
    ...options,
    deferInit: true,
    initGate: gate,
  });

  return {
    client,
    resolveInit: () => gate.resolve(),
  };
}

function buildChapterRows(books: MockBook[]): MockQueryRow[] {
  return books.flatMap((book) =>
    Array.from({ length: book.chapterCount }, (_, index) => ({
      book_id: book.id,
      book_name: book.name,
      testament: book.testament,
      chapter: index + 1,
      verse_count: verseCountFor(book.id, index + 1),
    })),
  );
}

function normalizeQuery(query: string): string {
  return query.replace(/\s+/g, ' ').trim();
}

function buildVerses(books: MockBook[], bookId: number, chapter: number): MockQueryRow[] {
  const book = books.find((entry) => entry.id === bookId);
  if (!book) return [];

  if (book.name === 'Matthew' && chapter === 1) {
    return [
      {
        verse: 1,
        text: 'The book of the generation of Jesus Christ, the son of David, the son of Abraham.',
      },
      {
        verse: 2,
        text: 'Abraham begat Isaac; and Isaac begat Jacob; and Jacob begat Judas and his brethren;',
      },
      {
        verse: 3,
        text: 'And Judas begat Phares and Zara of Thamar; and Phares begat Esrom; and Esrom begat Aram;',
      },
    ];
  }

  if (book.name === 'John' && chapter === 3) {
    return [
      {
        verse: 16,
        text: 'For God so loved the world, that he gave his only begotten Son.',
      },
      {
        verse: 17,
        text: 'For God sent not his Son into the world to condemn the world; but that the world through him might be saved.',
      },
      {
        verse: 18,
        text: 'He that believeth on him is not condemned: but he that believeth not is condemned already.',
      },
    ];
  }

  return Array.from({ length: verseCountFor(bookId, chapter) }, (_, index) => ({
    verse: index + 1,
    text: buildVerseText(book.name, chapter, index + 1),
  }));
}

function buildGreekRows(books: MockBook[], bookId: number, chapter: number): MockQueryRow[] {
  const book = books.find((entry) => entry.id === bookId);
  if (!book || book.testament !== 'NT') return [];

  return buildSourceRows(verseCountFor(bookId, chapter), (verse) =>
    greekTextFor(book.name, chapter, verse),
  );
}

function buildHebrewRows(books: MockBook[], bookId: number, chapter: number): MockQueryRow[] {
  const book = books.find((entry) => entry.id === bookId);
  if (!book || book.testament !== 'OT') return [];

  return buildSourceRows(verseCountFor(bookId, chapter), (verse) =>
    hebrewTextFor(book.name, chapter, verse),
  );
}

function buildLxxRows(books: MockBook[], bookId: number, chapter: number): MockQueryRow[] {
  const book = books.find((entry) => entry.id === bookId);
  if (!book || book.testament !== 'OT') return [];

  return buildSourceRows(verseCountFor(bookId, chapter), (verse) =>
    lxxTextFor(book.name, chapter, verse),
  );
}

function buildSourceRows(
  verseCount: number,
  getText: (verse: number) => string,
): MockQueryRow[] {
  return Array.from({ length: verseCount }, (_, verseIndex) => {
    const verse = verseIndex + 1;
    return getText(verse)
      .split(/\s+/)
      .filter(Boolean)
      .map((text, wordIndex) => ({
        verse,
        word_num: wordIndex + 1,
        text,
      }));
  }).flat();
}

function buildSearchRows(
  books: MockBook[],
  params: unknown[] = [],
  limit: number | null = 10,
): MockQueryRow[] {
  const tokens = decodeFtsMatchExpression(params[0]);

  return books
    .flatMap((book) =>
      Array.from({ length: book.chapterCount }, (_, chapterIndex) => chapterIndex + 1).flatMap(
        (chapter) =>
          buildVerses(books, book.id, chapter).map((verse) => ({
            book_id: book.id,
            book_name: book.name,
            chapter,
            verse: verse.verse,
            text: verse.text,
          })),
      ),
    )
    .filter((row) =>
      tokens.every((token) =>
        tokenizeForSearch(String(row.text)).some((word) => word.startsWith(token)),
      ),
    )
    .sort(
      (left, right) =>
        Number(left.book_id) - Number(right.book_id) ||
        Number(left.chapter) - Number(right.chapter) ||
        Number(left.verse) - Number(right.verse),
    )
    .slice(0, limit ?? undefined);
}

function buildCrossReferenceRows(
  books: MockBook[],
  params: unknown[] = [],
): MockCrossReferenceRow[] {
  const bookId = Number(params[0]);
  const chapter = Number(params[1]);
  const rows: MockCrossReferenceRow[] = [];

  for (const reference of crossReferencesFor(bookId, chapter)) {
    const targetBook = books.find((entry) => entry.id === reference.toBookId);
    const targetVerses = targetBook
      ? buildVerses(books, reference.toBookId, reference.toChapter).filter(
          (verse) =>
            Number(verse.verse) >= reference.toVerse &&
            Number(verse.verse) <= (reference.toVerseEnd ?? reference.toVerse),
        )
      : [];

    if (targetVerses.length === 0) {
      rows.push({
        id: reference.id,
        from_verse: reference.fromVerse,
        to_book_id: reference.toBookId,
        to_book_name: targetBook?.name ?? `Book ${reference.toBookId}`,
        to_chapter: reference.toChapter,
        to_verse: reference.toVerse,
        to_verse_end: reference.toVerseEnd,
        votes: reference.votes,
        target_verse: null,
        target_text: null,
      });
      continue;
    }

    for (const verse of targetVerses) {
      rows.push({
        id: reference.id,
        from_verse: reference.fromVerse,
        to_book_id: reference.toBookId,
        to_book_name: targetBook?.name ?? `Book ${reference.toBookId}`,
        to_chapter: reference.toChapter,
        to_verse: reference.toVerse,
        to_verse_end: reference.toVerseEnd,
        votes: reference.votes,
        target_verse: Number(verse.verse),
        target_text: String(verse.text),
      });
    }
  }

  return rows;
}

function verseCountFor(bookId: number, chapter: number): number {
  if (bookId === 40 && chapter === 1) return 3;
  if (bookId === 43 && chapter === 3) return 3;
  return 2;
}

function buildVerseText(bookName: string, chapter: number, verse: number): string {
  const usesStoryFixture = [
    'Genesis',
    'Psalms',
    'Matthew',
    'Mark',
    'Luke',
    'John',
    'Acts',
  ].includes(bookName);
  const base = usesStoryFixture
    ? `${bookName} ${chapter}:${verse} story sample text for the reader shell.`
    : `${bookName} ${chapter}:${verse} reading sample text for the reader shell.`;

  if (bookName === 'Mark' || bookName === 'Luke' || bookName === 'John' || bookName === 'Acts') {
    return Array.from({ length: 22 }, () => base).join(' ');
  }

  return base;
}

function greekTextFor(bookName: string, chapter: number, verse: number): string {
  if (bookName === 'Matthew' && chapter === 1 && verse === 1) {
    return 'Βίβλος γενέσεως Ἰησοῦ Χριστοῦ υἱοῦ Δαυὶδ υἱοῦ Ἀβραάμ.';
  }

  if (bookName === 'John' && chapter === 3 && verse === 16) {
    return 'Οὕτως γὰρ ἠγάπησεν ὁ θεὸς τὸν κόσμον.';
  }

  return `${bookName} ${chapter}:${verse} greek sample text.`;
}

function hebrewTextFor(bookName: string, chapter: number, verse: number): string {
  if (bookName === 'Genesis' && chapter === 1 && verse === 1) {
    return 'בְּ/רֵאשִׁית בָּרָא אֱלֹהִים אֵת הַשָּׁמַיִם וְ/אֵת הָ/אָרֶץ׃';
  }

  return `עברית ${bookName} ${chapter}:${verse}`;
}

function lxxTextFor(bookName: string, chapter: number, verse: number): string {
  if (bookName === 'Genesis' && chapter === 1 && verse === 1) {
    return 'Ἐν ἀρχῇ ἐποίησεν ὁ θεὸς τὸν οὐρανὸν καὶ τὴν γῆν.';
  }

  return `${bookName} ${chapter}:${verse} septuagint sample text.`;
}

function decodeFtsMatchExpression(value: unknown): string[] {
  return String(value ?? '')
    .split(/\s+AND\s+/i)
    .map((token) => token.replace(/\*+$/g, '').trim())
    .filter(Boolean);
}

function tokenizeForSearch(value: string): string[] {
  return value
    .normalize('NFKC')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

function extractSearchLimit(sql: string): number | null {
  const match = sql.match(/\bLIMIT\s+(\d+)\b/i);
  if (!match) {
    return null;
  }

  return Number(match[1]);
}

interface MockCrossReference {
  id: number;
  fromVerse: number;
  toBookId: number;
  toChapter: number;
  toVerse: number;
  toVerseEnd: number | null;
  votes: number;
}

interface MockCrossReferenceRow {
  id: number;
  from_verse: number;
  to_book_id: number;
  to_book_name: string;
  to_chapter: number;
  to_verse: number;
  to_verse_end: number | null;
  votes: number;
  target_verse: number | null;
  target_text: string | null;
}

function crossReferencesFor(bookId: number, chapter: number): MockCrossReference[] {
  if (bookId === 1 && chapter === 1) {
    return [
      {
        id: 2,
        fromVerse: 1,
        toBookId: 43,
        toChapter: 3,
        toVerse: 16,
        toVerseEnd: 17,
        votes: 361,
      },
      {
        id: 3,
        fromVerse: 1,
        toBookId: 40,
        toChapter: 1,
        toVerse: 1,
        toVerseEnd: 3,
        votes: 148,
      },
    ];
  }

  if (bookId === 40 && chapter === 1) {
    return [
      {
        id: 230217,
        fromVerse: 1,
        toBookId: 43,
        toChapter: 3,
        toVerse: 16,
        toVerseEnd: null,
        votes: 29,
      },
    ];
  }

  return [];
}

function createDeferredGate(): DeferredGate {
  let resolve = () => {};
  const promise = new Promise<void>((nextResolve) => {
    resolve = nextResolve;
  });

  return { promise, resolve };
}

class MockDatabaseClient implements DatabaseLike {
  status = $state<DbState>({ state: 'idle' });

  constructor(
    private readonly books: MockBook[],
    private readonly initGate: DeferredGate,
    initialStatus: DbState,
    private readonly hasExplicitStatus: boolean,
  ) {
    this.status = initialStatus;
  }

  async init(): Promise<void> {
    if (!this.hasExplicitStatus && this.status.state === 'idle') {
      this.status = { state: 'initializing' };
    }

    await this.initGate.promise;

    if (!this.hasExplicitStatus) {
      this.status = { state: 'ready' };
    }
  }

  async checkForUpdates(): Promise<boolean> {
    return false;
  }

  async query<T extends object>(sql: string, params: unknown[] = []): Promise<T[]> {
    const normalized = sql.replace(/\s+/g, ' ').trim();

    if (normalized === normalizeQuery(CATALOG_QUERY)) {
      return buildChapterRows(this.books) as T[];
    }

    if (normalized === normalizeQuery(CHAPTER_VERSES_QUERY)) {
      return buildVerses(this.books, Number(params[0]), Number(params[1])) as T[];
    }

    if (normalized.includes('FROM kjv_verses_fts f JOIN kjv_verses v ON v.id = f.rowid')) {
      return buildSearchRows(this.books, params, extractSearchLimit(normalized)) as T[];
    }

    if (normalized.includes('FROM cross_references cr')) {
      return buildCrossReferenceRows(this.books, params) as T[];
    }

    if (normalized.includes('FROM greek_nt_words')) {
      return buildGreekRows(this.books, Number(params[0]), Number(params[1])) as T[];
    }

    if (normalized.includes('FROM hebrew_ot_words')) {
      return buildHebrewRows(this.books, Number(params[0]), Number(params[1])) as T[];
    }

    if (normalized.includes('FROM lxx_words')) {
      return buildLxxRows(this.books, Number(params[0]), Number(params[1])) as T[];
    }

    throw new Error(`Unexpected mock query: ${normalized}`);
  }
}
